import { MarkerType } from '@xyflow/react';


export const TRACE_NODE_WIDTH = 236;
export const TRACE_NODE_HEIGHT = 76;

const COLUMN_GAP = 92;
const ROW_GAP = 28;
const SUCCESSFUL_ACTION_STATUSES = new Set([
  'completed',
  'succeeded'
]);
const EDGE_COLORS = {
  completed: 'var(--success)',
  error: 'var(--danger)',
  failed: 'var(--danger)',
  matched: 'var(--success)',
  passed: 'var(--success)',
  skipped: 'var(--warning)',
  succeeded: 'var(--success)'
};

const STATUS_LABELS = {
  completed: '已完成',
  delayed: '已延迟',
  error: '错误',
  failed: '失败',
  matched: '已匹配',
  partial: '部分完成',
  passed: '通过',
  running: '执行中',
  skipped: '已跳过',
  succeeded: '成功',
  unmatched: '未匹配'
};

const statusLabel = (status) => STATUS_LABELS[status] || status || '未知';

const traceRecords = (value) => (
  Array.isArray(value)
    ? value.filter(
      (item) => item !== null
        && typeof item === 'object'
        && !Array.isArray(item)
    )
    : []
);

const valueSummary = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') {
    if (value.truncated) return '内容已截断';
    if (typeof value.name === 'string') return value.name;
    if (typeof value.displayName === 'string') return value.displayName;
    if (typeof value.value === 'string') return value.value;
    if (Array.isArray(value)) {
      return value.map(valueSummary).join(', ');
    }
    return JSON.stringify(value);
  }
  return String(value);
};

const traceReason = (node) => (
  node.reason
  || node.reason_code
  || (
    ['error', 'failed', 'skipped', 'unmatched'].includes(node.status)
    && node.error
  )
);

const withReason = (summary, node) => {
  const reason = traceReason(node);
  if (node.status === 'skipped' && reason) {
    return valueSummary(reason);
  }
  return reason ? `${summary} · ${valueSummary(reason)}` : summary;
};

const summarizeTraceNode = (type, node) => {
  if (type === 'start') {
    return node.input?.issue_data_source
      ? `数据来源: ${node.input.issue_data_source}`
      : `来源: ${node.source || '-'}`;
  }
  if (type === 'finish') {
    return node.error || `耗时 ${node.duration_ms ?? 0} ms`;
  }
  if (type === 'group') {
    return withReason(`${traceRecords(node.rules).length} 条规则`, node);
  }
  if (type === 'rule') {
    const conditionCount = traceRecords(node.condition_groups)
      .reduce(
        (count, group) => count + traceRecords(group.conditions).length,
        0
      );
    return withReason(
      `${conditionCount} 个条件 · ${traceRecords(node.actions).length} 个动作`,
      node
    );
  }
  if (type === 'conditionGroup') {
    return withReason(
      `${String(node.logic || 'and').toUpperCase()} · ${traceRecords(node.conditions).length} 个条件`,
      node
    );
  }
  if (type === 'condition') {
    const actualValue = node.raw_actual_value ?? node.normalized_value;
    return withReason(
      `实际 ${valueSummary(actualValue)} · 期望 ${valueSummary(node.expected_value)}`,
      node
    );
  }
  if (type === 'action') {
    return node.message || node.error || node.type || '动作';
  }
  return '';
};

const createGraphNode = (id, type, title, traceNode, depth) => ({
  id,
  type: 'trace',
  position: { x: 0, y: 0 },
  data: {
    id,
    nodeType: type,
    title,
    status: traceNode.status || 'running',
    statusLabel: statusLabel(traceNode.status),
    summary: summarizeTraceNode(type, traceNode),
    durationMs: traceNode.duration_ms,
    traceNode
  },
  _depth: depth,
  _children: []
});

const nodeKey = (value, fallback) => (
  value === null || value === undefined || value === ''
    ? fallback
    : value
);

export const buildTraceGraph = (attempt = {}) => {
  const traceAttempt = attempt !== null
    && typeof attempt === 'object'
    && !Array.isArray(attempt)
    ? attempt
    : {};
  const nodes = [];
  const edges = [];
  let edgeIndex = 0;
  let maxDepth = 0;

  const addNode = (id, type, title, traceNode, depth) => {
    const node = createGraphNode(id, type, title, traceNode, depth);
    nodes.push(node);
    maxDepth = Math.max(maxDepth, depth);
    return node;
  };

  const connect = (source, target, status, includeInLayout = true) => {
    const edgeStatus = status || 'running';
    edges.push({
      id: `edge-${edgeIndex += 1}`,
      source: source.id,
      target: target.id,
      animated: false,
      className: `execution-trace-edge status-${edgeStatus}`,
      data: { status: edgeStatus },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 8,
        height: 8,
        color: EDGE_COLORS[edgeStatus] || 'var(--text-muted)'
      }
    });
    if (includeInLayout) {
      source._children.push(target);
    }
  };

  const start = addNode(
    'start',
    'start',
    '开始执行',
    traceAttempt,
    0
  );
  const successfulActions = [];

  traceRecords(traceAttempt.rule_groups).forEach((group, groupIndex) => {
    const groupId = nodeKey(group.id, groupIndex + 1);
    const groupNode = addNode(
      `group-${groupId}`,
      'group',
      group.name || `规则组 ${groupId}`,
      group,
      1
    );
    connect(start, groupNode, group.status);

    const rules = traceRecords(group.rules);
    rules.forEach((rule, ruleIndex) => {
      const ruleId = nodeKey(
        rule.id,
        `${groupId}-${ruleIndex + 1}`
      );
      const ruleNode = addNode(
        `rule-${ruleId}`,
        'rule',
        rule.name || `规则 ${ruleId}`,
        rule,
        2
      );
      connect(groupNode, ruleNode, rule.status);

      const conditionGroups = traceRecords(rule.condition_groups);
      conditionGroups.forEach((conditionGroup, groupConditionIndex) => {
        const conditionGroupIndex = nodeKey(
          conditionGroup.index,
          groupConditionIndex + 1
        );
        const conditionGroupNode = addNode(
          `condition-group-${ruleId}-${conditionGroupIndex}`,
          'conditionGroup',
          `条件组 ${conditionGroupIndex}`,
          conditionGroup,
          3
        );
        connect(ruleNode, conditionGroupNode, conditionGroup.status);

        const conditions = traceRecords(conditionGroup.conditions);
        conditions.forEach(
          (condition, conditionIndex) => {
            const savedIndex = nodeKey(
              condition.index,
              conditionIndex + 1
            );
            const conditionNode = addNode(
              `condition-${ruleId}-${conditionGroupIndex}-${savedIndex}`,
              'condition',
              condition.field_name
                || condition.field_id
                || `条件 ${savedIndex}`,
              condition,
              4
            );
            connect(
              conditionGroupNode,
              conditionNode,
              condition.status
            );
          }
        );
      });

      const legacyConditions = traceRecords(rule.conditions);
      legacyConditions.forEach((condition, conditionIndex) => {
        const savedIndex = nodeKey(
          condition.index,
          conditionIndex + 1
        );
        const conditionNode = addNode(
          `condition-${ruleId}-legacy-${savedIndex}`,
          'condition',
          condition.field_name
            || condition.field_id
            || `条件 ${savedIndex}`,
          condition,
          4
        );
        connect(ruleNode, conditionNode, condition.status);
      });

      const actions = traceRecords(rule.actions);
      actions.forEach((action, actionIndex) => {
        const savedIndex = nodeKey(action.index, actionIndex + 1);
        const actionNode = addNode(
          `action-${ruleId}-${savedIndex}`,
          'action',
          action.type || `动作 ${savedIndex}`,
          action,
          5
        );
        connect(ruleNode, actionNode, action.status);
        if (SUCCESSFUL_ACTION_STATUSES.has(action.status)) {
          successfulActions.push(actionNode);
        }
      });
    });
  });

  traceRecords(traceAttempt.direct_actions).forEach((action, actionIndex) => {
    const savedIndex = nodeKey(action.index, actionIndex + 1);
    const actionNode = addNode(
      `direct-action-${savedIndex}`,
      'action',
      action.type || `动作 ${savedIndex}`,
      action,
      1
    );
    connect(start, actionNode, action.status);
    if (SUCCESSFUL_ACTION_STATUSES.has(action.status)) {
      successfulActions.push(actionNode);
    }
  });

  const finish = successfulActions.length
    ? addNode(
      'finish',
      'finish',
      '执行完成',
      {
        status: 'succeeded',
        duration_ms: traceAttempt.duration_ms
      },
      maxDepth + 1
    )
    : null;
  successfulActions.forEach((node) => {
    connect(node, finish, node.data.status, false);
  });

  let nextLeafY = 0;
  const placeNode = (node, visiting = new Set()) => {
    if (Number.isFinite(node._placedY)) return node._placedY;
    if (visiting.has(node.id)) return nextLeafY;
    visiting.add(node.id);

    const childYs = node._children.map(
      (child) => placeNode(child, visiting)
    );
    const y = childYs.length
      ? (Math.min(...childYs) + Math.max(...childYs)) / 2
      : nextLeafY++;
    node._placedY = y;
    node.position = {
      x: node._depth * (TRACE_NODE_WIDTH + COLUMN_GAP),
      y: y * (TRACE_NODE_HEIGHT + ROW_GAP)
    };
    visiting.delete(node.id);
    return y;
  };
  placeNode(start);

  if (finish && !Number.isFinite(finish._placedY)) {
    const terminalYs = successfulActions.map(
      (node) => node._placedY || 0
    );
    finish.position = {
      x: finish._depth * (TRACE_NODE_WIDTH + COLUMN_GAP),
      y: (
        (Math.min(...terminalYs) + Math.max(...terminalYs)) / 2
      ) * (TRACE_NODE_HEIGHT + ROW_GAP)
    };
  }

  return {
    nodes: nodes.map(({ _depth, _children, _placedY, ...node }) => node),
    edges
  };
};
